import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import MobileSticky from "../components/MobileSticky";
import Seo from "../components/Seo";
import { COMPANY } from "../data/site";

function Section({ title, children }) {
  return (
    <section className="mt-10 md:mt-14" data-testid={`privacy-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <h2 className="font-display text-2xl md:text-3xl tracking-tight text-[#0B1426]">{title}</h2>
      <div className="hairline-gold mt-4 w-10" />
      <div className="mt-5 md:mt-6 space-y-4 text-[15px] md:text-base leading-[1.75] text-[#4F5B6A]">
        {children}
      </div>
    </section>
  );
}

export default function Privacy() {
  return (
    <div data-testid="page-privacy">
      <Seo
        title="Privacy Policy — Connolly's Painting Solutions"
        description="Read the Privacy Policy for Connolly's Painting Solutions. How we collect, use, store and protect your personal information."
        path="/privacy-policy"
      />
      <Header />

      <section className="section bg-[#F9F8F6]">
        <div className="container-cps max-w-3xl">
          <div className="overline">Legal</div>
          <h1 className="font-display text-4xl md:text-6xl mt-4 tracking-tight text-[#0B1426] leading-tight">
            Privacy Policy.
          </h1>
          <p className="mt-5 text-sm text-[#4F5B6A]/80 tracking-wide uppercase">
            Last updated: 4 August 2026
          </p>

          <div className="hairline-gold my-10 md:my-12" />

          <p className="text-[15px] md:text-base leading-[1.75] text-[#4F5B6A]">
            Connolly's Painting Solutions respects your privacy and is committed to handling personal information responsibly.
          </p>

          <Section title="Information we collect">
            <p>
              When you contact us, request a quote, submit an enquiry through our website, or complete one of our forms or advertisements on platforms such as Facebook or Instagram, we may collect information including your name, email address, phone number, suburb or location, details about the painting services you require, and any other information you choose to provide.
            </p>
            <p>
              Our website may also collect basic technical information, such as browser, device and website usage information, through cookies or similar technologies.
            </p>
          </Section>

          <Section title="How we use your information">
            <p>
              We may use your personal information to respond to enquiries, provide and follow up on quotes, communicate with you about painting services, arrange appointments or work, provide customer service, operate and improve our website and advertising, and manage our business.
            </p>
            <p>
              We primarily service customers across the Sunshine Coast and North Brisbane, Queensland.
            </p>
          </Section>

          <Section title="Facebook, Instagram and Meta advertising">
            <p>
              We may use advertising and lead-generation services provided by Meta, including Facebook and Instagram.
            </p>
            <p>
              If you submit your details through a Meta lead form or advertisement, Meta may provide that information to Connolly's Painting Solutions so that we can respond to your enquiry or quote request.
            </p>
            <p>
              Your use of Facebook, Instagram and other Meta services is also subject to Meta's own privacy practices.
            </p>
          </Section>

          <Section title="Sharing of personal information">
            <p>We do not sell or rent your personal information.</p>
            <p>
              We may disclose information to trusted service providers where reasonably necessary to operate our business, website, communications or advertising. These providers may include website hosting providers, email and communications services, advertising platforms such as Meta, and other technology providers.
            </p>
            <p>We may also disclose information where required or authorised by law.</p>
          </Section>

          <Section title="Storage and security">
            <p>
              We take reasonable steps to protect personal information from misuse, interference, loss, unauthorised access, modification or disclosure.
            </p>
            <p>
              However, no method of electronic transmission or storage can be guaranteed to be completely secure.
            </p>
          </Section>

          <Section title="Cookies and website analytics">
            <p>
              Our website may use cookies and similar technologies to operate the website, understand how visitors use it, improve its performance and measure advertising effectiveness.
            </p>
            <p>
              Third-party services used on our website may also use cookies or similar technologies in accordance with their own privacy policies.
            </p>
          </Section>

          <Section title="Accessing or correcting your information">
            <p>
              You may contact us if you would like to request access to personal information we hold about you or ask us to correct information that is inaccurate or out of date.
            </p>
            <p>
              You may also contact us if you have a question or concern about how your personal information has been handled.
            </p>
          </Section>

          <Section title="Contact us">
            <p>For privacy-related enquiries, contact:</p>
            <div className="mt-4 space-y-1.5 text-[#0B1426]">
              <div className="font-display text-lg">Connolly's Painting Solutions</div>
              <div>
                Email:{" "}
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="text-[#0B1426] underline decoration-[#C5A059] decoration-2 underline-offset-4 hover:text-[#C5A059]"
                  data-testid="privacy-email-link"
                >
                  {COMPANY.email}
                </a>
              </div>
              <div>
                Website:{" "}
                <a
                  href="https://connollyspaintingsolutions.com.au"
                  className="text-[#0B1426] underline decoration-[#C5A059] decoration-2 underline-offset-4 hover:text-[#C5A059]"
                  data-testid="privacy-website-link"
                >
                  connollyspaintingsolutions.com.au
                </a>
              </div>
            </div>
          </Section>

          <div className="hairline-gold my-12" />

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm text-[#4F5B6A]">
            <Link
              to="/"
              className="text-[#0B1426] hover:text-[#C5A059] font-medium"
              data-testid="privacy-back-home"
            >
              ← Back to home
            </Link>
            <Link
              to="/quote"
              className="text-[#0B1426] hover:text-[#C5A059] font-medium"
              data-testid="privacy-get-quote"
            >
              Get a Free Quote →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <MobileSticky />
    </div>
  );
}
